import { ASSET_KEYS, validateProfile, verifyPackage } from './profile';
import { canonical, hashObject } from './identity';

export const PACKAGE_RESPONSE_LIMIT = 768 * 1024;
const copy = value => JSON.parse(JSON.stringify(value));
const hash = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const text = (value, max = 120) => typeof value === 'string' && value.length > 0 && value.length <= max && value.trim() === value && /^[ -~]+$/.test(value);
const tagIdentity = value => typeof value === 'string' ? value.trim().toUpperCase() : '';

export function validatePackageReference(value) {
  if (!value || Object.keys(value).sort().join('|') !== 'contentHash|documentNumber|id|revision'
    || !/^[a-f0-9]{24}$/.test(value.id) || !text(value.documentNumber) || !text(value.revision) || !hash(value.contentHash)) {
    throw new Error('The automatic package identity is incomplete. Check the package again.');
  }
  return copy(value);
}

export function inspectPackageResponse(body, requestedTag) {
  if (!body || !['ready', 'blocked', 'missing', 'ambiguous'].includes(body.status) || !Array.isArray(body.blockers) || body.blockers.length > 100) {
    throw new Error('Northstar returned an invalid package readiness response.');
  }
  const subject = body.subject;
  if (subject && (!text(subject.deviceId) || !text(subject.serviceTagNumber) || !text(subject.productNumber)
    || tagIdentity(subject.serviceTagNumber) !== tagIdentity(requestedTag))) throw new Error('The automatic package does not match the requested subject.');
  const safeSubject = subject ? { deviceId: subject.deviceId, serviceTagNumber: subject.serviceTagNumber,
    productNumber: subject.productNumber, productFamily: typeof subject.productFamily === 'string' ? subject.productFamily.slice(0, 120) : '' } : null;
  const blockers = body.blockers.map(item => {
    if (!item || !text(item.code) || !text(item.message, 1000)) throw new Error('Northstar returned an invalid readiness gap.');
    return { code: item.code, message: item.message };
  });
  if (body.status !== 'ready') return { status: body.status, subject: safeSubject, blockers, packageRef: body.packageRef ? validatePackageReference(body.packageRef) : null };
  if (!subject || blockers.length || !hash(body.profileHash)) throw new Error('The ready package is missing its verified subject or profile identity.');
  const packageRef = validatePackageReference(body.packageRef);
  validateProfile(body.profileSnapshot, { hardware: true });
  if (!Array.isArray(body.assets) || body.assets.length !== ASSET_KEYS.length || new Set(body.assets.map(asset => asset.key)).size !== ASSET_KEYS.length) {
    throw new Error('The automatic package must contain all four base assets.');
  }
  const assets = ASSET_KEYS.map(key => {
    const asset = body.assets.find(item => item.key === key), expected = body.profileSnapshot.assets[key];
    if (!asset || asset.name !== expected.name || asset.sha256 !== expected.sha256 || !Number.isInteger(asset.bytes) || asset.bytes < 1 || asset.bytes > 256 * 1024) {
      throw new Error(`The automatic package has invalid ${key} metadata.`);
    }
    return { key, name: asset.name, sha256: asset.sha256, bytes: asset.bytes };
  });
  return { status: 'ready', subject: safeSubject, blockers, packageRef, profile: copy(body.profileSnapshot), profileHash: body.profileHash, assets };
}

export async function loadAutomaticPackage(body, requestedTag, readAsset, checkCurrent = () => {}) {
  const ready = inspectPackageResponse(body, requestedTag); checkCurrent();
  if (ready.status !== 'ready') return { readiness: ready, configuration: null };
  if (await hashObject(ready.profile) !== ready.profileHash) throw new Error('Automatic profile hash mismatch. Check the package again.');
  const assets = {};
  for (const asset of ready.assets) {
    checkCurrent();
    assets[asset.key] = await readAsset(`/configuration-packages/${ready.packageRef.id}/assets/${asset.key}?subjectServiceTag=${encodeURIComponent(requestedTag)}`, asset.bytes);
    checkCurrent();
    if (!(assets[asset.key] instanceof Uint8Array) || assets[asset.key].length !== asset.bytes) throw new Error(`Automatic asset byte count mismatch: ${asset.key}.`);
  }
  const verified = await verifyPackage(ready.profile, assets, { hardware: true }); checkCurrent();
  if (verified.profileHash !== ready.profileHash) throw new Error('Automatic profile hash mismatch.');
  return { readiness: ready, configuration: { ...verified, configurationPackage: ready.packageRef } };
}

export async function recheckAutomaticPackage(body, subject, configuration) {
  const latest = inspectPackageResponse(body, subject.serviceTagNumber);
  if (latest.status !== 'ready') throw new Error('This package is no longer ready. Check the subject again and resolve the readiness gaps.');
  if (latest.subject.deviceId !== subject.deviceId || latest.subject.productNumber !== subject.productNumber
    || canonical(latest.packageRef) !== canonical(configuration.configurationPackage) || latest.profileHash !== configuration.profileHash
    || await hashObject(latest.profile) !== configuration.profileHash
    || latest.assets.some(asset => asset.bytes !== configuration.assets[asset.key]?.length)) {
    throw new Error('The subject or package version changed. Check the subject again and review the new package before starting.');
  }
  return latest;
}
